// retention-benchmarks.js - Tier 2 Data & Research
export const content = {
  heroStyle: 'stats',
  tag: 'Benchmarks',
  stats: [
    { number: '75-85%', label: 'Avg Annual Retention', color: 'blue' },
    { number: '90%+', label: 'Top Performer Target', color: 'green' },
    { number: '85%', label: 'First-Year Target', color: 'amber' },
    { number: '5-7x', label: 'Cost to Replace vs. Retain', color: 'purple' }
  ],

  html: `
<p>You can't improve what you don't measure, and you can't know if you're doing well unless you have benchmarks to compare against. This guide provides the retention benchmarks you need to evaluate your performance and set meaningful goals.</p>

<h2>Industry Average Retention Rates</h2>

<h3>Overall Annual Retention</h3>
<p>Based on available research and industry data, the typical Medicare agent/agency retention rate falls in the <strong>75-85% range annually</strong>. This means losing 15-25% of clients each year to churn.</p>

<p>Breaking that down:</p>
<ul>
<li><strong>Below average:</strong> Under 75% retention (losing 25%+ annually)</li>
<li><strong>Average:</strong> 75-85% retention (losing 15-25% annually)</li>
<li><strong>Above average:</strong> 85-90% retention (losing 10-15% annually)</li>
<li><strong>Excellent:</strong> 90%+ retention (losing less than 10% annually)</li>
</ul>

<h3>First-Year Retention</h3>
<p>New enrollees churn at higher rates than established clients. National data suggests about 15.6% of new MA enrollees leave within the first year.</p>

<p>First-year retention benchmarks:</p>
<ul>
<li><strong>Below average:</strong> Under 80% (losing 20%+ of new enrollees)</li>
<li><strong>Average:</strong> 80-85% (losing 15-20% of new enrollees)</li>
<li><strong>Good:</strong> 85-90% (losing 10-15% of new enrollees)</li>
<li><strong>Excellent:</strong> 90%+ (losing less than 10% of new enrollees)</li>
</ul>

<h3>Long-Term Retention (5-Year)</h3>
<p>Research shows about 49% of new enrollees have changed insurance by year 5. This means roughly half your new clients will be gone within 5 years at industry average rates.</p>

<p>5-year retention benchmarks:</p>
<ul>
<li><strong>Average:</strong> ~51% (keeping about half)</li>
<li><strong>Good:</strong> 60-70%</li>
<li><strong>Excellent:</strong> 70%+</li>
</ul>

<h2>Benchmarks by Segment</h2>

<h3>Medicare Advantage vs. Medigap</h3>
<p>MA clients generally have higher churn than Medigap clients due to:</p>
<ul>
<li>Annual plan changes creating shopping opportunities</li>
<li>More aggressive carrier marketing</li>
<li>AEP/OEP switching flexibility</li>
</ul>

<p>Medigap clients who stay past underwriting period tend to be stickier, though premium fatigue can drive switching to MA.</p>

<h3>Dual Eligible vs. Non-Dual</h3>
<p>Research shows dual eligibles have higher churn:</p>
<ul>
<li><strong>Non-dual 5-year retention:</strong> ~52%</li>
<li><strong>Dual 5-year retention:</strong> ~47%</li>
</ul>

<p>If your book has significant dual-eligible population, expect higher baseline churn and plan for more intensive retention efforts.</p>

<h3>By Plan Type</h3>
<p>Retention varies by plan. Track your book by plan to identify:</p>
<ul>
<li>Which plans retain best</li>
<li>Which plans have chronic churn problems</li>
<li>Whether certain plans need extra retention attention</li>
</ul>

<h2>Benchmarks by Agency Size</h2>

<h3>Solo Agents</h3>
<p>Solo agents typically have more personal relationships but less systematic processes. Retention often depends heavily on the individual agent's habits.</p>
<ul>
<li>Wide variation: 70-95% depending on agent</li>
<li>Best performers: agents with consistent touchpoint systems</li>
<li>Worst performers: agents who only show up during AEP</li>
</ul>

<h3>Small Agencies (2-10 agents)</h3>
<p>Small agencies can maintain personal touch while adding some systematization.</p>
<ul>
<li>Typical range: 75-90%</li>
<li>Challenge: consistency across agents</li>
<li>Opportunity: shared systems and processes</li>
</ul>

<h3>Large Agencies/Call Centers</h3>
<p>Scale creates both challenges and opportunities for retention.</p>
<ul>
<li>Typical range: 70-85%</li>
<li>Challenge: personal relationships at scale</li>
<li>Opportunity: sophisticated systems and data</li>
</ul>

<h2>Financial Benchmarks</h2>

<h3>Cost to Acquire vs. Retain</h3>
<p>Industry rule of thumb: it costs <strong>5-7x more to acquire a new client than to retain an existing one</strong>.</p>
<ul>
<li>New client acquisition cost: $150-300 (leads, time, marketing)</li>
<li>Retention touchpoint cost: $25-50 (time for calls, systems)</li>
</ul>

<h3>Lifetime Value Impact</h3>
<p>A 10% improvement in retention can increase client lifetime value by 30-50%. Each year retained adds significant value:</p>
<ul>
<li>Year 1 client value: 1x annual commission</li>
<li>Year 3 client value: 3x (if retained)</li>
<li>Year 5 client value: 5x (if retained)</li>
<li>Year 10 client value: 10x (if retained)</li>
</ul>

<h3>Revenue Impact of Retention Improvement</h3>
<p>For a 200-client book at $300/year average commission:</p>
<ul>
<li>80% retention: $12,000 lost annually</li>
<li>85% retention: $9,000 lost annually</li>
<li>90% retention: $6,000 lost annually</li>
</ul>
<p>Each 5% retention improvement = $3,000/year in protected revenue.</p>

<h2>Setting Your Goals</h2>

<p>Based on these benchmarks, here are reasonable retention goals:</p>

<h3>If You're Below Average (under 75%)</h3>
<ul>
<li>Year 1 goal: Reach 80%</li>
<li>Focus: Basic systematization (quarterly touchpoints, birthday calls)</li>
<li>Low-hanging fruit: Contact clients you haven't talked to in 6+ months</li>
</ul>

<h3>If You're Average (75-85%)</h3>
<ul>
<li>Year 1 goal: Reach 87-88%</li>
<li>Focus: Identifying at-risk clients, proactive plan change monitoring</li>
<li>Upgrade: Better CRM, automated reminders</li>
</ul>

<h3>If You're Above Average (85-90%)</h3>
<ul>
<li>Year 1 goal: Reach 92%+</li>
<li>Focus: Optimizing touchpoints, predictive risk identification</li>
<li>Upgrade: AI-powered tools, sophisticated analytics</li>
</ul>

<h2>Tracking Your Progress</h2>

<p>To benchmark yourself, you need to measure:</p>

<h3>Essential Metrics</h3>
<ul>
<li><strong>Overall retention rate:</strong> Clients retained / Clients at start of period</li>
<li><strong>First-year retention:</strong> New enrollees retained through year 1</li>
<li><strong>Segment retention:</strong> Retention by plan, by client type, by enrollment year</li>
<li><strong>Churn reasons:</strong> Why clients who left actually left</li>
</ul>

<h3>Calculation Method</h3>
<p>Annual retention rate = (Clients at end of year who were also at start) / (Clients at start of year) × 100</p>

<p>Don't count new enrollees in either number—you're measuring how many existing clients stayed, not total client count.</p>

<h2>The Improvement Trajectory</h2>

<p>Realistic retention improvement:</p>
<ul>
<li>Year 1 with focus: 5-8% improvement</li>
<li>Year 2 with systems: 3-5% additional improvement</li>
<li>Year 3+: 1-2% incremental improvement</li>
</ul>

<p>Most agents can move from average (80%) to good (88-90%) within 2-3 years of focused effort. Moving from good to excellent requires sophisticated tools and sustained discipline.</p>
  `,

  faqs: [
    {
      question: "What's a good retention rate for a Medicare agent?",
      answer: "Industry average is 75-85% annual retention. Above average is 85-90%. Excellent is 90%+. If you're below 75%, you have significant room for improvement with basic systematization."
    },
    {
      question: "What's the typical first-year retention rate for new Medicare enrollees?",
      answer: "National data shows about 15.6% of new MA enrollees leave within the first year. Average first-year retention is around 80-85%. Top performers achieve 90%+ first-year retention through strong onboarding and early touchpoints."
    },
    {
      question: "How much does it cost to replace a lost client vs. retain them?",
      answer: "Industry rule of thumb: 5-7x more to acquire than retain. New client acquisition typically costs $150-300 (leads, time, marketing). Retention touchpoints cost $25-50 (time for calls, systems). The math strongly favors retention investment."
    },
    {
      question: "How do I calculate my retention rate?",
      answer: "Annual retention rate = (Clients at year end who were also there at year start) / (Clients at year start) × 100. Don't include new enrollees in either number—you're measuring how many existing clients stayed."
    },
    {
      question: "What's a realistic retention improvement goal?",
      answer: "With focused effort: Year 1 can see 5-8% improvement, Year 2 adds 3-5%, Year 3+ adds 1-2% incrementally. Most agents can move from average (80%) to good (88-90%) within 2-3 years of systematic work."
    }
  ],

  sources: [
    {
      title: "Turnover Among New Medicare Advantage Enrollees",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9586458/",
      publisher: "AJMC/PMC",
      accessedDate: "2024-12-10"
    },
    {
      title: "Medicare Advantage Disenrollment Patterns",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10457718/",
      publisher: "JAMA Health Forum/PMC",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Knowing the benchmarks is step one. Tracking your actual performance against them is step two. Pro IRP provides retention analytics that show exactly where you stand—overall, by segment, by plan—so you can set goals and measure progress.`
};

export default content;
